// Typed realtime event definitions.
//
// These mirror the API's `domain_events` table — every event the server emits
// via `emitEvent` (apps/api/src/lib/events.ts) lands in this row shape, plus
// a small set of ephemeral broadcast-only events (presence, "actor recording
// now") that are never persisted.
//
// Channel naming: `org:<organization_id>:<topic>` where topic ∈
//   `takes` | `comments` | `project_progress` | `notifications`
//
// Subscribers should treat unknown `event_type` strings gracefully — new
// events will be added on the API side ahead of the SPA picking them up.

export type RealtimeTopic = 'takes' | 'comments' | 'project_progress' | 'notifications'

/**
 * Canonical set of persisted domain event types. The string union is the
 * authoritative client-side view; the API uses the same strings via
 * `emitEvent({ event_type: '…' })`.
 */
export type RealtimeEventType =
  // Take lifecycle
  | 'TakeRecorded'
  | 'TakeUploaded'
  | 'TakeApproved'
  | 'TakeRejected'
  // Batch lifecycle
  | 'BatchSubmitted'
  | 'BatchApproved'
  // Stem render pipeline
  | 'StemRenderStarted'
  | 'StemRenderCompleted'
  | 'StemRenderFailed'
  // Comments / mentions
  | 'CommentCreated'
  | 'CommentResolved'
  // Project / processing pipeline
  | 'ProjectProcessingStarted'
  | 'ProjectProcessingCompleted'
  // Pickups + finance
  | 'PickupRequested'
  | 'MilestonePaid'

/**
 * Persisted event shape — matches a row from the `domain_events` table sent
 * over Supabase Realtime as a `postgres_changes` payload. `payload` is open
 * so we don't pin a JSON schema across services; consumers should narrow as
 * needed when they inspect it.
 */
export interface RealtimeEvent {
  event_type: RealtimeEventType | (string & {})
  aggregate_type: string
  aggregate_id: string
  organization_id: string
  payload: Record<string, unknown>
  emitted_at: string
}

/**
 * Ephemeral broadcast events that don't pass through `domain_events`. Fired
 * from the API via `broadcast()` (apps/api/src/lib/realtime.ts). Keep this
 * list short — anything with audit value belongs in `domain_events`.
 */
export type EphemeralBroadcastEvent =
  | 'actor_recording_started'
  | 'actor_recording_stopped'
  | 'cursor_moved'

/**
 * Type guard for a persisted RealtimeEvent. Useful when an `onEvent`
 * callback receives the union of postgres-changes + broadcast payloads.
 */
export function isPersistedRealtimeEvent(value: unknown): value is RealtimeEvent {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v['event_type'] === 'string' &&
    typeof v['aggregate_type'] === 'string' &&
    typeof v['aggregate_id'] === 'string' &&
    typeof v['organization_id'] === 'string' &&
    typeof v['emitted_at'] === 'string'
  )
}
